/**
 * Ready checks, and what the room can all play.
 *
 * Two small things a party needs before it starts, both of which used to
 * happen by typing "r?" into chat and counting the replies.
 *
 * The ready check is deliberately three answers rather than two: "two
 * minutes" is the true state of half a squad at any given moment, and a check
 * that only offers ready or nothing makes people answer ready and then not be
 * there. It expires by itself after five minutes, so an unfinished check
 * never sits on the party for the rest of the evening.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Party } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { useToast } from './ui';

type Answer = 'ready' | 'soon' | 'away';

const ANSWERS: { id: Answer; label: string; icon: string; tone: string }[] = [
  { id: 'ready', label: 'Ready', icon: 'checkCirc', tone: 'var(--good)' },
  { id: 'soon', label: 'Two minutes', icon: 'clock', tone: 'var(--warn)' },
  { id: 'away', label: 'Stepped away', icon: 'ban', tone: 'var(--text-mute)' }
];

/** "1:42", counting down to when the check lapses. */
function useCountdown(endsAt: number | undefined): string {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!endsAt) return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [endsAt]);
  if (!endsAt) return '';
  const left = Math.max(0, endsAt - Date.now());
  const s = Math.floor(left / 1000);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

export function ReadyCheck({ party, canManage }: { party: Party; canManage: boolean }) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const check = party.ready && party.ready.endsAt > Date.now() ? party.ready : null;
  const left = useCountdown(check?.endsAt);

  async function call(body: Record<string, unknown>) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post<{ party: Party }>('/api/parties/' + party.id + '/ready', body);
      queryClient.setQueryData(['party', party.id], (old: { party: Party; messages: unknown } | undefined) =>
        old ? { ...old, party: res.party } : old);
      void queryClient.invalidateQueries({ queryKey: ['party', party.id] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!party.isMember) return null;

  /* Nothing running: the host gets the button, everyone else gets nothing -
     a row saying "no ready check" is noise. */
  if (!check) {
    if (!canManage || party.memberCount < 2) return null;
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => void call({ start: true })}
        className={cn(
          'flex h-10 flex-none items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-4 text-[13.5px] font-semibold outline-none',
          'bg-surface-2 text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] transition-colors hover:bg-surface-3 hover:text-ink',
          'focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-50'
        )}
      >
        <Icon name="checkCirc" size={16} />
        Ready check
      </button>
    );
  }

  const mine = me ? check.answers[me.id] : undefined;
  const counts = { ready: 0, soon: 0, away: 0 };
  for (const a of Object.values(check.answers)) counts[a] += 1;
  const waiting = party.memberCount - Object.keys(check.answers).length;
  const allIn = counts.ready === party.memberCount;

  return (
    <section
      aria-label="Ready check"
      className={cn(
        'flex-none rounded-[calc(16px*var(--radius-mul,1))] p-3 animate-[fade-in_220ms_ease-out] motion-reduce:animate-none',
        allIn ? 'bg-[color-mix(in_srgb,var(--good)_12%,var(--bg-1))] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--good)_40%,transparent)]'
          : 'bg-surface-1 shadow-[inset_0_0_0_1px_var(--accent-line)]'
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className={cn('grid h-8 w-8 flex-none place-items-center rounded-full', allIn ? 'bg-good text-[var(--bg-0)]' : 'bg-accent text-accent-fg')}>
          <Icon name={allIn ? 'check' : 'checkCirc'} size={17} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-bold text-ink">
            {allIn ? 'Everyone is ready' : 'Ready check'}
          </span>
          <span className="block truncate text-[12.5px] text-mute">
            {counts.ready} ready
            {counts.soon ? ' · ' + counts.soon + ' in two minutes' : ''}
            {counts.away ? ' · ' + counts.away + ' away' : ''}
            {waiting > 0 ? ' · waiting on ' + waiting : ''}
            {' · ' + left}
          </span>
        </span>

        <div className="flex flex-none items-center gap-1.5">
          {ANSWERS.map((a) => {
            const on = mine === a.id;
            return (
              <button
                key={a.id}
                type="button"
                disabled={busy}
                aria-pressed={on}
                onClick={() => void call({ answer: a.id })}
                title={a.label}
                className={cn(
                  'flex h-9 items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] px-3 text-[12.5px] font-bold outline-none',
                  'transition-[background-color,color,box-shadow] duration-[160ms] focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-50',
                  on ? 'text-[var(--bg-0)]' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                )}
                style={on ? { background: a.tone } : undefined}
              >
                <Icon name={a.icon} size={14} />
                <span className="hidden @sm:inline">{a.label}</span>
              </button>
            );
          })}
          {canManage ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void call({ stop: true })}
              title="End the ready check"
              className="grid h-9 w-9 place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Icon name="close" size={14} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Who said what, so the host does not have to count. */}
      <ul className="m-0 mt-2.5 flex list-none flex-wrap gap-1.5 p-0">
        {party.members.map((m) => {
          const answer = check.answers[m.userId];
          const look = ANSWERS.find((a) => a.id === answer);
          return (
            <li
              key={m.userId}
              className="flex items-center gap-1.5 rounded-full bg-surface-2 py-[3px] pr-2.5 pl-[3px] text-[12px] font-semibold text-dim"
              title={m.user ? m.user.displayName + ' · ' + (look?.label ?? 'has not answered') : undefined}
            >
              {m.user ? <Avatar user={m.user} size={20} /> : null}
              <span className="max-w-[90px] truncate">{m.user?.displayName ?? 'Player'}</span>
              {look ? <Icon name={look.icon} size={13} style={{ color: look.tone }} /> : <span className="text-ghost">·</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * What everybody in the room actually owns.
 *
 * Built from the games each member put on their profile, which is the only
 * honest source the app has - so it says how many people it knows about
 * rather than pretending to be a library scan.
 */
export function SharedGames({ party }: { party: Party }) {
  const catalog = useSession((s) => s.catalog);
  const [open, setOpen] = useState(false);

  const said = party.members.filter((m) => (m.user?.games?.length ?? 0) > 0);
  const counts = new Map<string, number>();
  for (const m of said) {
    for (const g of m.user!.games!) counts.set(g.id, (counts.get(g.id) ?? 0) + 1);
  }

  const rows = [...counts.entries()]
    .map(([id, n]) => ({ game: catalog?.games.find((g) => g.id === id), id, n }))
    .filter((r) => r.game)
    .sort((a, b) => b.n - a.n || a.game!.name.localeCompare(b.game!.name));

  const everyone = rows.filter((r) => r.n === said.length && said.length > 1);
  if (!said.length) return null;

  return (
    <section aria-labelledby="party-shared-games" className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left outline-none"
      >
        <Icon name="dice" size={15} className="flex-none text-accent" />
        <h3 id="party-shared-games" className="m-0 flex-1 text-[13.5px] font-bold text-ink">
          What you can all play
        </h3>
        <span className="text-[12px] font-semibold text-mute">
          {everyone.length ? everyone.length + ' game' + (everyone.length === 1 ? '' : 's') : 'nothing in common'}
        </span>
        <Icon name="chevronD" size={14} className={cn('flex-none text-ghost transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        <div className="mt-2.5">
          {everyone.length ? (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {everyone.map((r) => (
                <li key={r.id} className="flex items-center gap-2 rounded-full bg-accent-soft py-1 pr-3 pl-1 text-[12.5px] font-bold text-accent">
                  <GameMark id={r.id} size={20} plain />
                  {r.game!.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-[12.5px] text-mute">
              Nothing that everyone has listed. The most popular here:
            </p>
          )}

          {rows.length && (!everyone.length || rows.length > everyone.length) ? (
            <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
              {rows.filter((r) => r.n < said.length).slice(0, 4).map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-[12.5px] text-dim">
                  <GameMark id={r.id} size={18} plain />
                  <span className="min-w-0 flex-1 truncate">{r.game!.name}</span>
                  <span className="flex-none text-mute">{r.n} of {said.length}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {said.length < party.memberCount ? (
            <p className="m-0 mt-2 text-[11.5px] text-ghost">
              Counted from the {said.length} of {party.memberCount} who listed their games.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
